import * as React from "react";
import Axios from "axios";
import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import TextField from "@/components/atoms/TextField";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import toast from "@/components/hoc/SnackbarAlert";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";
import {
  createMlitFlowPersonAPI,
  deleteMlitFlowPersonAPI,
} from "@/util/api/accidentReportPersons";
import {
  EMAIL_MAX_LENGTH,
  NAME_MAX_LENGTH,
  PHONE_NUMBER_MAX_LENGTH,
  hasFormError,
  sanitizePhoneNumberInput,
  validateAccidentReportPersonForm,
  type AccidentReportPersonFormError,
  type MlitFlowPerson,
} from "@/models/accidentReportPersons";
import { AccidentReportPersonsConstants as Constants } from "@/pages/AccidentReportPersons/AccidentReportPersonsConstants";

type MlitPersonRowProps = {
  person: MlitFlowPerson;
  officeName: string;
  onDelete: (person: MlitFlowPerson) => void;
};

// 検索条件の切替など一覧と無関係な状態変化で全行分のクロージャが再生成されるのを防ぐため、
// 行を React.memo 化する。onDelete が安定している（親で useCallback 済み）ことが前提
const MlitPersonRow = React.memo(
  ({ person, officeName, onDelete }: MlitPersonRowProps) => (
    <TableRow hover sx={{ wordBreak: "break-all" }}>
      <TableCell>{person.officeName ?? officeName}</TableCell>
      <TableCell>{person.name ?? "-"}</TableCell>
      <TableCell>{person.email ?? "-"}</TableCell>
      <TableCell>{person.phoneNumber ?? "-"}</TableCell>
      <TableCell sx={{ textAlign: "right", pr: 0 }}>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {/* 登録内容の変更はGO社内のみ。事業者には導線自体を出さない。 */}
          <CommonButton btnType="Delete" onClickBtn={() => onDelete(person)} />
        </Stack>
      </TableCell>
    </TableRow>
  ),
);

export type MlitFlowResponsiblesSectionProps = {
  // 事故報告責任者一覧と同じ営業所絞り込み（単一選択）に連動させる。
  // 登録先もこの営業所に固定し、別営業所へ登録する手段は持たせない。
  officeId: number;
  officeName: string;
  persons: MlitFlowPerson[];
  onReload: () => void;
};

type MlitFormInput = {
  name: string;
  email: string;
  phoneNumber: string;
};

const emptyInput: MlitFormInput = {
  name: "",
  email: "",
  phoneNumber: "",
};

/**
 * 国交省フロー組込み責任者セクション（事業者向け）。
 * 事故報告責任者とは独立した別テーブルで管理し、人数上限チェック・氏名重複チェック・
 * 同一人物判定・eラーニング案内・Slack通知はいずれも行わない。
 * 事業者が行えるのは登録と削除のみで、登録内容の変更はGO社内へ依頼する。
 */
export const MlitFlowResponsiblesSection = (
  props: MlitFlowResponsiblesSectionProps,
) => {
  const { officeId, officeName, persons, onReload } = props;

  const [createOpen, setCreateOpen] = React.useState(false);
  const [createInput, setCreateInput] =
    React.useState<MlitFormInput>(emptyInput);
  const [createError, setCreateError] =
    React.useState<AccidentReportPersonFormError>({});
  const [deleteTarget, setDeleteTarget] = React.useState<MlitFlowPerson | null>(
    null,
  );

  const [creating, createPerson] = useLoadingPromise(createMlitFlowPersonAPI);
  const [deleting, deletePerson] = useLoadingPromise(deleteMlitFlowPersonAPI);

  const handleClickRowDelete = React.useCallback((person: MlitFlowPerson) => {
    setDeleteTarget(person);
  }, []);

  // const handleClickCreate = () => {
  //   setCreateInput(emptyInput);
  //   setCreateError({});
  //   setCreateOpen(true);
  // };

  const handleCreateConfirm = async () => {
    if (creating) return;
    // 必須・形式・文字数上限は事故報告責任者の登録フォームと同一条件にそろえる
    const errors = validateAccidentReportPersonForm({
      ...createInput,
      officeId,
    });
    if (hasFormError(errors)) {
      setCreateError(errors);
      return;
    }
    setCreateError({});

    try {
      await createPerson({
        officeId,
        name: createInput.name.trim(),
        email: createInput.email.trim(),
        phoneNumber: createInput.phoneNumber.trim(),
      });
      setCreateOpen(false);
      toast.success(Constants.MlitCreateSuccess);
      onReload();
    } catch (error) {
      const message =
        Axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : Constants.FetchError;
      toast.error(message);
      console.error("==Error when create mlitFlowPerson==", error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || deleting) return;
    try {
      await deletePerson(deleteTarget.id);
      setDeleteTarget(null);
      toast.success(Constants.MlitDeleteSuccess);
      onReload();
    } catch (error) {
      const message =
        Axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : Constants.FetchError;
      toast.error(message);
      console.error("==Error when delete mlitFlowPerson==", error);
    }
  };

  const deleteConfirmMessage = deleteTarget
    ? Constants.MlitDeleteConfirmMessageFormat(
        deleteTarget.officeName ?? officeName,
        deleteTarget.name ?? "-",
      )
    : null;

  return (
    <>
      <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: "bold" }}>
              {Constants.MlitSectionTitle}
            </Typography>
            {Constants.MlitSectionNotes.map((note) => (
              <Typography
                key={note}
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {note}
              </Typography>
            ))}
          </Box>
          {/* <Button
            label={Constants.MlitCreateButton}
            sx={{ height: 35, flexShrink: 0 }}
            onClick={handleClickCreate}
          /> */}
        </Box>

        <Table size="small" sx={{ mt: 1 }}>
          <TableHead className="list-head">
            <TableRow>
              <TableCell>{Constants.TableMlitOfficeHeader}</TableCell>
              <TableCell>{Constants.TableMlitNameHeader}</TableCell>
              <TableCell>{Constants.TableMlitEmailHeader}</TableCell>
              <TableCell>{Constants.TableMlitPhoneHeader}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {persons.map((item) => (
              <MlitPersonRow
                key={item.id}
                person={item}
                officeName={officeName}
                onDelete={handleClickRowDelete}
              />
            ))}
          </TableBody>
        </Table>
        {persons.length === 0 && (
          <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
            {Constants.MlitSectionEmpty}
          </Box>
        )}
      </Paper>

      {/* 登録先は表示中の営業所で固定。営業所を選ばせると、登録直後に一覧へ出ない状態が起きる。 */}
      <ConfirmDialog
        visible={createOpen}
        headerTitle={Constants.MlitCreateDialogTitle}
        headerType="Normal"
        type="Normal"
        textAlign="left"
        onOk={handleCreateConfirm}
        onCancel={() => setCreateOpen(false)}
      >
        <Stack spacing={2} sx={{ minWidth: 320, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {Constants.MlitCreateDialogDescription}
          </Typography>
          <Box>
            <Typography variant="body2" color="text.secondary">
              {Constants.MlitOfficeLabel}
            </Typography>
            <Typography>{officeName}</Typography>
          </Box>
          <TextField
            label={Constants.MlitNameLabel}
            value={createInput.name}
            error={!!createError.name}
            errorLabel={
              createError.name === "format"
                ? Constants.NameFormatError
                : Constants.NameRequiredError
            }
            onChange={(e) =>
              setCreateInput({ ...createInput, name: e.target.value })
            }
            inputProps={{ maxLength: NAME_MAX_LENGTH }}
            sx={{ width: "100%" }}
          />
          <TextField
            label={Constants.MlitEmailLabel}
            type="email"
            value={createInput.email}
            error={!!createError.email}
            errorLabel={
              createError.email === "format"
                ? Constants.EmailFormatError
                : Constants.EmailRequiredError
            }
            onChange={(e) =>
              setCreateInput({ ...createInput, email: e.target.value })
            }
            inputProps={{ maxLength: EMAIL_MAX_LENGTH }}
            sx={{ width: "100%" }}
          />
          <TextField
            label={Constants.MlitPhoneLabel}
            type="tel"
            value={createInput.phoneNumber}
            error={!!createError.phoneNumber}
            errorLabel={
              createError.phoneNumber === "format"
                ? Constants.PhoneFormatError
                : Constants.PhoneRequiredError
            }
            onChange={(e) =>
              setCreateInput({
                ...createInput,
                phoneNumber: sanitizePhoneNumberInput(e.target.value),
              })
            }
            inputProps={{ maxLength: PHONE_NUMBER_MAX_LENGTH }}
            sx={{ width: "100%" }}
          />
        </Stack>
      </ConfirmDialog>

      <ConfirmDialog
        visible={deleteTarget !== null}
        headerTitle={Constants.MlitDeleteDialogTitle}
        type="Delete"
        showIcon
        textAlign="left"
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      >
        {deleteConfirmMessage && (
          <Box sx={{ textAlign: "left" }}>
            <Typography sx={{ fontWeight: "bold" }}>
              {deleteConfirmMessage.question}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: "pre-wrap", mt: 1 }}
            >
              {deleteConfirmMessage.note}
            </Typography>
            <Typography sx={{ fontWeight: "bold", mt: 1 }}>
              {deleteConfirmMessage.contact}
            </Typography>
          </Box>
        )}
      </ConfirmDialog>
    </>
  );
};
